import type { ReactElement } from 'react';
import React from 'react';
import type { ErrorBoundaryProps } from 'react-error-boundary';
import { Box } from 'components/Box';
import { Text } from 'components/Text';

type GetErrorBoundaryPropsWithFallbackOptions = {
  existingProps: Partial<ErrorBoundaryProps>;
  fallback?: ReactElement<any>;
};

const defaultFallback = (
  <Box alignItems="center" display="flex" height="100%" justifyContent="center">
    <Text>Something went wrong.</Text>
  </Box>
);

/**
 * A function making sure that the error boundary props include a fallback _if_
 * one isn't specified.
 */
export const getErrorBoundaryPropsWithFallback = ({
  existingProps,
  fallback = defaultFallback,
}: GetErrorBoundaryPropsWithFallbackOptions) => {
  /**
   * These props will be passed to the react-error-boundary ErrorBoundary
   * component, which requires one of these three props to be passed: fallback,
   * fallbackRender, and fallbackComponent.
   *
   * Fallback is the most straightforward to default to, but it will override
   * the other fallback props if it has a value, so we need to make sure that no
   * fallback props have been passed.
   *
   * see: https://github.com/bvaughn/react-error-boundary/blob/master/src/index.tsx#L132-L143
   */
  if (
    !existingProps?.fallback &&
    !existingProps?.fallbackRender &&
    !existingProps?.FallbackComponent
  ) {
    return { ...existingProps, fallback } as ErrorBoundaryProps;
  }

  return existingProps as ErrorBoundaryProps;
};

export const testExports = {
  defaultFallback,
};
